import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { resourceView } from '../../utils/viewer';
import type { ResourceViewProps } from '../../utils/viewer';
import type { Resource } from '../../interfaces';

export interface OpenResourceProps extends ResourceViewProps {
  /** The full resource when the caller has it — saves a fetch and carries the
   *  print-order fields (EnableAddToCart, HasLocation, …) the preview needs. */
  resource?: Resource;
  /** Numeric resource id (`Resource.ID`) for callers without the full object —
   *  the resource endpoint resolves by this, not by the `ResourceID` string. */
  id?: Resource['ID'];
  /** Hide the Add to Cart action in the preview — for callers whose data
   *  can't support it (e.g. global search: Algolia hits lack the cart fields). */
  hidePrintOrderAction?: boolean;
}

export type OpenResource = (props: OpenResourceProps) => void;

interface ResourceOpenerContextValue {
  openResource: OpenResource;
}

/** Legacy behaviour: the web viewer in a new tab. */
export const openResourceInViewer: OpenResource = props => {
  resourceView(props);
};

// DialogProvider renders dialogs as siblings of its children, i.e. outside any
// provider mounted below it — a click inside a dialog (local printer, resource
// children, product details, compose drawer) never sees ResourceOpenerProvider.
// The webapp therefore also registers its opener here, and the context default
// delegates to it, falling back to the legacy viewer when nothing is registered.
let registeredOpener: OpenResource | null = null;

export const registerResourceOpener = (opener: OpenResource | null) => {
  registeredOpener = opener;
};

const ResourceOpenerContext = createContext<ResourceOpenerContextValue>({
  openResource: props => (registeredOpener ?? openResourceInViewer)(props)
});

interface ResourceOpenerProviderProps {
  openResource: OpenResource;
  children: ReactNode;
}

export const ResourceOpenerProvider = ({
  openResource,
  children
}: ResourceOpenerProviderProps) => {
  const value = useMemo(() => ({ openResource }), [openResource]);

  return (
    <ResourceOpenerContext.Provider value={value}>
      {children}
    </ResourceOpenerContext.Provider>
  );
};

export const useResourceOpener = () => useContext(ResourceOpenerContext);
